import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

/**
 * DynamicMetaTags component for SEO
 * Props override default values from PublicSettings context
 * @param {string} title - Page title (overrides siteName)
 * @param {string} description - Meta description (overrides ogDescription)
 * @param {string} image - OG/Twitter image URL (overrides ogImage)
 * @param {string} url - Page URL (overrides ogUrl)
 * @param {string} type - OG type (overrides ogType, default: 'article' for pages with title prop)
 * @param {string} canonical - Canonical URL (defaults to url prop or window.location)
 */
const DynamicMetaTags = ({ title, description, image, url, type, canonical, alternates }) => {
  const {
    siteName,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    twitterCard,
    twitterImage
  } = usePublicSettings();

  // Ensure all values are strings (convert null/undefined to empty string)
  const safeString = (value) => value || '';

  // Use props if provided, otherwise fall back to context values
  const finalTitle = title || ogTitle || siteName;
  const finalDescription = description || ogDescription;
  const finalImage = image || ogImage;
  const finalUrl = url || ogUrl;
  const finalType = type || (title ? 'article' : ogType);

  // Build canonical URL: explicit prop > url prop > current path on jetogo.com
  const getCanonicalUrl = () => {
    if (canonical) return canonical;
    if (url) return url;
    if (typeof window !== 'undefined') {
      // Always use production domain for canonical, strip trailing slash except root
      const path = window.location.pathname;
      const cleanPath = path === '/' ? '/' : path.replace(/\/$/, '');
      return `https://jetogo.com${cleanPath}`;
    }
    return 'https://jetogo.com';
  };
  const canonicalUrl = getCanonicalUrl();

  return (
    <Helmet>
      <title>{safeString(finalTitle)}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content={safeString(finalType)} />
      <meta property="og:url" content={safeString(finalUrl)} />
      <meta property="og:title" content={safeString(finalTitle)} />
      <meta property="og:description" content={safeString(finalDescription)} />
      {finalImage ? <meta property="og:image" content={safeString(finalImage)} /> : null}
      <meta property="og:site_name" content={safeString(siteName)} />
      <meta name="twitter:card" content={safeString(twitterCard)} />
      <meta name="twitter:url" content={safeString(finalUrl)} />
      <meta name="twitter:title" content={safeString(finalTitle)} />
      <meta name="twitter:description" content={safeString(finalDescription)} />
      {(finalImage) ? (
        <meta name="twitter:image" content={safeString(finalImage)} />
      ) : null}
      <meta name="description" content={safeString(finalDescription)} />
      {alternates && alternates.map(alt => (
        <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}
    </Helmet>
  );
};

export default DynamicMetaTags;
